// Rich text editor (what you see is what you get): bold, italic, underline, strikethrough, headings, lists, emoji.
// The text is stored as Markdown (shown with the same safe renderer everywhere and in the mails).
import { useEffect, useState } from 'react';
import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Markdown } from '@tiptap/markdown';
import { CharacterCount, Placeholder } from '@tiptap/extensions';
import { legacyHtml } from './lib/md';

const EMOJI = [
  '🏊',
  '🚴',
  '🏃',
  '🏅',
  '⏱️',
  '💪',
  '🔥',
  '👍',
  '🎉',
  '🍻',
  '☕',
  '🌞',
  '🌧️',
  '❄️',
  '📍',
  '🗓️',
  '⏰',
  '⚠️',
  '✅',
  '❌',
  '🚗',
  '🏟️',
  '🌊',
  '👟',
];

export function MdEditor({
  id,
  label,
  value,
  onChange,
  rows = 8,
  placeholder,
  maxLength,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  placeholder?: string;
  maxLength?: number;
}) {
  const [emoji, setEmoji] = useState(false);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        code: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        link: { openOnClick: false, autolink: true, protocols: ['http', 'https'], defaultProtocol: 'https' },
      }),
      Markdown,
      Placeholder.configure({ placeholder: placeholder ?? '' }),
      CharacterCount.configure({ limit: maxLength ?? null }),
    ],
    // no injected <style> tag: the app's Content Security Policy only allows style sheets from our own files
    injectCSS: false,
    content: legacyHtml(value), // texts from the old Sportplaner may contain <b>…</b> etc.
    contentType: 'markdown',
    editorProps: {
      attributes: {
        id,
        class: 'rich desc',
        role: 'textbox',
        'aria-multiline': 'true',
        'aria-label': label,
        'data-rows': String(Math.min(12, Math.max(3, rows))), // height via CSS (inline styles are blocked by the CSP)
      },
    },
    onUpdate: ({ editor: e }) => onChange(e.getMarkdown()),
  });
  // text replaced from outside (e.g. a template loaded later): show it
  useEffect(() => {
    if (editor && value !== editor.getMarkdown() && !editor.isFocused)
      editor.commands.setContent(legacyHtml(value), { contentType: 'markdown', emitUpdate: false });
  }, [editor, value]);
  // which formats are active at the cursor (buttons look pressed)
  const active = useEditorState({
    editor,
    selector: ({ editor: e }) =>
      e
        ? {
            h1: e.isActive('heading', { level: 1 }),
            h2: e.isActive('heading', { level: 2 }),
            h3: e.isActive('heading', { level: 3 }),
            b: e.isActive('bold'),
            i: e.isActive('italic'),
            u: e.isActive('underline'),
            s: e.isActive('strike'),
            ul: e.isActive('bulletList'),
            ol: e.isActive('orderedList'),
          }
        : null,
  });
  const run = (f: (c: NonNullable<typeof editor>['commands']) => void) => () => {
    if (!editor) return;
    editor.commands.focus();
    f(editor.commands);
  };
  const btn = (key: keyof NonNullable<typeof active>, ariaLabel: string, content: React.ReactNode, f: () => void) => (
    <button type="button" onClick={f} aria-label={ariaLabel} aria-pressed={!!active?.[key]}>
      {content}
    </button>
  );
  return (
    <div className="fl">
      <span className="fl-l" id={`${id}-label`}>
        {label}
      </span>
      <div className="md-bar" role="toolbar" aria-label="Formatierung">
        {btn(
          'h1',
          'Große Überschrift',
          <b>H1</b>,
          run(c => c.toggleHeading({ level: 1 })),
        )}
        {btn(
          'h2',
          'Mittlere Überschrift',
          <b>H2</b>,
          run(c => c.toggleHeading({ level: 2 })),
        )}
        {btn(
          'h3',
          'Kleine Überschrift',
          <b>H3</b>,
          run(c => c.toggleHeading({ level: 3 })),
        )}
        {btn(
          'b',
          'Fett',
          <b>B</b>,
          run(c => c.toggleBold()),
        )}
        {btn(
          'i',
          'Kursiv',
          <i>I</i>,
          run(c => c.toggleItalic()),
        )}
        {btn(
          'u',
          'Unterstrichen',
          <u>U</u>,
          run(c => c.toggleUnderline()),
        )}
        {btn(
          's',
          'Durchgestrichen',
          <s>S</s>,
          run(c => c.toggleStrike()),
        )}
        {btn(
          'ul',
          'Aufzählung',
          '• ≡',
          run(c => c.toggleBulletList()),
        )}
        {btn(
          'ol',
          'Nummerierte Liste',
          '1.≡',
          run(c => c.toggleOrderedList()),
        )}
        <button type="button" onClick={() => setEmoji(!emoji)} aria-label="Emoji einfügen" aria-expanded={emoji}>
          🙂
        </button>
      </div>
      {emoji && (
        <div className="emoji-pop">
          {EMOJI.map(x => (
            <button
              key={x}
              type="button"
              onClick={() => {
                editor?.chain().focus().insertContent(x).run();
                setEmoji(false);
              }}
            >
              {x}
            </button>
          ))}
        </div>
      )}
      <EditorContent editor={editor} />
    </div>
  );
}
